import React, { Suspense } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthProvider } from '@guildeducationinc/guild-auth';
import { DelayedLoader } from '@guildeducationinc/recess';
import { AnalyticsDelayWrapper } from '@guildeducationinc/mfe-utils';
import { datadogRum } from '@datadog/browser-rum';
import GqlProvider from '@src/components/GqlProvider/GqlProvider';
import EmployerContentProvider from '@src/context/EmployerContentContext';
import ModalProvider from '@src/context/ModalContext';
import ErrorBannerProvider from '@src/context/ErrorBannerContext';
import ConfirmationDialogProvider from '@src/context/ConfirmationDialogContext';
import EnrollmentsProvider from '@src/context/EnrollmentsContext';
import MemberPlanProvider from '@src/context/MemberPlanContext';
import MemberTraitsProvider from '@src/context/MemberTraitsContext';
import { authRedirectCallback } from '@src/utilities/authUtility';
import ErrorBoundary from '@shared/ErrorBoundary/ErrorBoundary';
import { PROFILE_COMPLETION_URL } from '@src/constants';
import ProfileCompletionProvider from '@src/context/ProfileCompletionContext';
import LearningLifecyclesProvider from '@src/context/LearningLifecyclesContext';
import ConfettiProvider from '@src/context/ConfettiContext';

// place all of your providers into this component
const Providers: React.FC = ({ children }) => {
  const history = useHistory();

  const getRedirectUri = () => {
    // to account for employer-specific subdomains like wm.guildeducation.com/verify
    return window.location.pathname.includes('/home')
      ? `${window.location.origin}/home`
      : window.location.origin;
  };

  return (
    <ErrorBoundary>
      <AnalyticsDelayWrapper>
        <Suspense fallback={<DelayedLoader loaded={false} label='Loading...' />}>
          <AuthProvider
            useRefreshTokens={true}
            audience={process.env.REACT_APP_AUTH0_AUDIENCE}
            domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
            client_id={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
            redirect_uri={getRedirectUri()}
            onRedirectCallback={(appState) => {
              authRedirectCallback(appState, history);
            }}
            forceRedirectForIncompleteProfile={true}
            completeProfileUrl={PROFILE_COMPLETION_URL}
            errorLogHandler={(message, exception) => {
              datadogRum.addError(exception, {
                message,
                type: 'warning',
              });
            }}
          >
            <GqlProvider>
              <MemberPlanProvider>
                <ProfileCompletionProvider>
                  <LearningLifecyclesProvider>
                    <MemberTraitsProvider>
                      <EmployerContentProvider>
                        <EnrollmentsProvider>
                          <ModalProvider>
                            <ConfirmationDialogProvider>
                              <ErrorBannerProvider>
                                <ConfettiProvider>{children}</ConfettiProvider>
                              </ErrorBannerProvider>
                            </ConfirmationDialogProvider>
                          </ModalProvider>
                        </EnrollmentsProvider>
                      </EmployerContentProvider>
                    </MemberTraitsProvider>
                  </LearningLifecyclesProvider>
                </ProfileCompletionProvider>
              </MemberPlanProvider>
            </GqlProvider>
          </AuthProvider>
        </Suspense>
      </AnalyticsDelayWrapper>
    </ErrorBoundary>
  );
};

export default Providers;
